import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Camera, Upload } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { hapticFeedback, isNative } from '../utils/mobile';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FoodCapture = ({ onCapture, token, dailyScansLeft }) => {
  const [capturedImage, setCapturedImage] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [mealType, setMealType] = useState('lunch');
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const { t } = useTranslation();

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setCapturedImage(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleCameraCapture = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setCapturedImage(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const analyzeMeal = async () => {
    if (!capturedImage) return;
    setAnalyzing(true);
    try {
      const response = await axios.post(
        `${API}/meals/analyze`,
        { image_base64: capturedImage, meal_type: mealType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.new_badges && response.data.new_badges.length > 0) {
        alert(`New badge${response.data.new_badges.length > 1 ? 's' : ''} earned!`);
      }
      onCapture(response.data.meal);
      setCapturedImage(null);
    } catch (error) {
      console.error('Error analyzing meal:', error);
      alert(error.response?.data?.detail || 'Failed to analyze meal. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold">Scan Your Meal</h2>
        {dailyScansLeft !== undefined && dailyScansLeft >= 0 && (
          <span className="text-xs text-gray-500 dark:text-gray-400" data-testid="scans-left">
            {dailyScansLeft} {t('foodCapture.scansLeft')}
          </span>
        )}
      </div>
      
      <div className="flex gap-2 mb-4">
        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
        <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleCameraCapture} className="hidden" />
        
        <button
          data-testid="food-capture-gallery-btn"
          onClick={() => fileInputRef.current?.click()}
          className="flex-1 py-3 px-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 transition-colors"
        >
          <Upload size={18} />
          {t('foodCapture.gallery')}
        </button>
        
        <button
          data-testid="food-capture-camera-btn"
          onClick={() => { hapticFeedback('medium'); cameraInputRef.current?.click(); }}
          className="flex-1 py-3 px-3 rounded-lg text-sm font-medium flex items-center justify-center gap-2 bg-[#FF6600] text-white hover:bg-[#e55a00] transition-colors"
        >
          <Camera size={18} />
          {t('foodCapture.takePhoto')}
        </button>
      </div>

      {!capturedImage ? (
        <div className="border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl p-8 text-center">
          <Camera size={48} className="mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500 dark:text-gray-400 text-sm mb-2">{t('foodCapture.photoPrompt')}</p>
          <p className="text-gray-400 text-xs">{t('foodCapture.supported')}</p>
        </div>
      ) : (
        <div>
          <img src={capturedImage} alt="Captured" className="w-full rounded-lg mb-3" />
          <div className="mb-3">
            <label className="text-sm font-medium block mb-1">{t('foodCapture.mealType')}</label>
            <select
              data-testid="food-capture-meal-type"
              value={mealType}
              onChange={(e) => setMealType(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#FF6600]"
            >
              <option value="breakfast">{t('foodCapture.breakfast')}</option>
              <option value="lunch">{t('foodCapture.lunch')}</option>
              <option value="dinner">{t('foodCapture.dinner')}</option>
              <option value="snack">{t('foodCapture.snack')}</option>
            </select>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="food-capture-retake-btn"
              onClick={() => setCapturedImage(null)}
              className="flex-1 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-medium text-sm"
              disabled={analyzing}
            >
              {t('foodCapture.retake')}
            </button>
            <button
              data-testid="food-capture-analyze-btn"
              onClick={analyzeMeal}
              className="flex-1 py-2 bg-[#FF6600] text-white rounded-lg font-semibold text-sm"
              disabled={analyzing}
            >
              {analyzing ? t('foodCapture.analyzing') : t('foodCapture.analyzeMeal')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FoodCapture;
